import { useCallback, useState } from 'react';
import { useAuthorityApi, formatAuthorityForDisplay } from '../utils/authorityApi';
import {
  buildAuthorityRecord,
  formatAuthorityForLinking
} from '../utils/authorityCreation';
import { getAuthoritySourceForField } from '../../../utils/configUtils';
import { getAuthorityTypeForField } from '../../../constants';

/**
 * Hook for automatic authority linking of a single field
 * Each TextFieldComponent uses this to process only its own field
 */
export const useFieldAutoLinking = ({
  fieldPath,
  textValue,
  index,
  formValues,
  controlledVocab,
  onLinkAuthority,
  onShowMessage,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { searchExactMatch, createAuthorityRecord } = useAuthorityApi();

  const processCurrentField = useCallback(async () => {
    if (!textValue || textValue.length < 3) {
      return { action: 'skipped', reason: 'text_too_short' };
    }

    setIsProcessing(true);

    try {
      // Determine authority type for this field
      const authorityType = getAuthorityTypeForField(
        fieldPath,
        formValues,
        index,
        controlledVocab,
      );
      const searchType = authorityType?.searchType || 'personalName';

      // Clean textValue: replace -- with space for authority search
      const cleanedTextValue = textValue.trim().replace(/--/g, ' ');

      // 1. Search for exact match first
      const exactResults = await searchExactMatch(
        cleanedTextValue,
        searchType,
        5
      );



      // Check for exact match only
      if (exactResults.authorities.length > 0) {
        const exactMatch = exactResults.authorities.find(auth => 
          auth.headingRef === cleanedTextValue
        );
        
        if (exactMatch) {
          // Found exact match - link it
          const authority = formatAuthorityForDisplay(exactMatch);
          


          // Apply the linking
          const [parentField, propName] = fieldPath.split('.');
          onLinkAuthority(parentField, index, propName, authority);
          
          // Show success message
          onShowMessage('success', 'ui-plugin-inventory-templates.authority.autoLinking.existingLinked', { term: textValue });

          return {
            action: 'linked_existing',
            authority,
            reason: 'exact_match_found',
          };
        }
        

      }


      
      const authoritySource = getAuthoritySourceForField(fieldPath, controlledVocab);
      const recordData = buildAuthorityRecord(
        cleanedTextValue,
        fieldPath,
        formValues,
        index,
        controlledVocab,
        authoritySource,
      );

      const response = await createAuthorityRecord(recordData);
      const newAuthority = formatAuthorityForLinking(
        response,
        cleanedTextValue,
        fieldPath,
        formValues,
        index,
        controlledVocab,
      );



      // Apply the linking
      const [parentField, propName] = fieldPath.split('.');
      onLinkAuthority(parentField, index, propName, newAuthority);
      
      // Show success message
      onShowMessage('success', 'ui-plugin-inventory-templates.authority.autoLinking.newCreated', { term: textValue });

      return {
        action: 'created_and_linked',
        authority: newAuthority,
        reason: 'no_exact_match_create_new',
      };

    } catch (error) {
      console.error('Error processing field:', error);
      return {
        action: 'error',
        reason: 'processing_failed',
        error: error.message,
      };
    } finally {
      setIsProcessing(false);
    }
  }, [
    fieldPath,
    textValue,
    index,
    formValues,
    controlledVocab,
    searchExactMatch,
    createAuthorityRecord,
    onLinkAuthority,
    onShowMessage
  ]);

  return {
    processCurrentField,
    isProcessing,
  };
};

export default useFieldAutoLinking;